import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { EncabezadoSeccion, Reveal } from "./motion";

function Lightbox({ fotos, inicial, alCerrar, p }) {
  const [ref, api] = useEmblaCarousel({ loop: true, startIndex: inicial });
  const [actual, setActual] = useState(inicial);

  useEffect(() => {
    if (!api) return;
    const alSeleccionar = () => setActual(api.selectedScrollSnap());
    api.on("select", alSeleccionar);
    alSeleccionar();
    return () => api.off("select", alSeleccionar);
  }, [api]);

  useEffect(() => {
    const raiz = document.documentElement;
    raiz.style.overflow = "hidden";
    return () => {
      raiz.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const alTeclear = (e) => {
      if (e.key === "Escape") alCerrar(actual);
      if (e.key === "ArrowLeft") api?.scrollPrev();
      if (e.key === "ArrowRight") api?.scrollNext();
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [alCerrar, actual, api]);

  return (
    <div
      className={`${p}-lightbox`}
      role="dialog"
      aria-modal="true"
      aria-label="Galería ampliada"
      data-testid={`${p}-lightbox`}
      onClick={(e) => {
        if (e.target === e.currentTarget) alCerrar(actual);
      }}
    >
      <button
        type="button"
        className={`${p}-lightbox-cerrar`}
        onClick={() => alCerrar(actual)}
        aria-label="Cerrar galería"
        data-testid={`${p}-lightbox-cerrar`}
      >
        <X strokeWidth={1.5} />
      </button>
      <div className={`${p}-lightbox-carrusel`} ref={ref}>
        <div className={`${p}-lightbox-pista`}>
          {fotos.map((foto, i) => (
            <div className={`${p}-lightbox-item`} key={i}>
              <img src={foto.src} alt={foto.alt || `Fotografía ${i + 1}`} draggable={false} />
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className={`${p}-lightbox-flecha ${p}-lightbox-flecha--izq`}
        onClick={() => api?.scrollPrev()}
        aria-label="Foto anterior"
        data-testid={`${p}-lightbox-anterior`}
      >
        <ChevronLeft strokeWidth={1.5} />
      </button>
      <button
        type="button"
        className={`${p}-lightbox-flecha ${p}-lightbox-flecha--der`}
        onClick={() => api?.scrollNext()}
        aria-label="Foto siguiente"
        data-testid={`${p}-lightbox-siguiente`}
      >
        <ChevronRight strokeWidth={1.5} />
      </button>
      <p className={`${p}-lightbox-contador`} data-testid={`${p}-lightbox-contador`} aria-live="polite">
        {actual + 1} / {fotos.length}
      </p>
    </div>
  );
}

// Galería compartida: carrusel táctil (máx. 4) + lightbox a pantalla completa.
// Cada plantilla define las clases con su prefijo: {p}-galeria, {p}-lightbox, etc.
export default function Galeria({ datos, seccion, p = "we" }) {
  const fotos = (datos.fotos?.galeria || []).slice(0, 4);
  const [ref, api] = useEmblaCarousel({ loop: true });
  const [actual, setActual] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!api) return;
    const alSeleccionar = () => setActual(api.selectedScrollSnap());
    api.on("select", alSeleccionar);
    alSeleccionar();
    return () => api.off("select", alSeleccionar);
  }, [api]);

  const alTeclear = useCallback(
    (e) => {
      if (e.key === "ArrowLeft") api?.scrollPrev();
      if (e.key === "ArrowRight") api?.scrollNext();
    },
    [api]
  );

  const cerrarLightbox = useCallback(
    (indice) => {
      api?.scrollTo(indice);
      setLightbox(null);
    },
    [api]
  );

  if (fotos.length === 0) return null;

  return (
    <section className={`${p}-seccion`} aria-label={seccion.titulo}>
      <div className={`${p}-seccion-interior`}>
        <EncabezadoSeccion p={p} etiqueta={seccion.etiqueta} titulo={seccion.titulo} testid={`${p}-titulo-galeria`} />
        <Reveal>
          <div
            className={`${p}-galeria`}
            ref={ref}
            role="region"
            aria-roledescription="carrusel"
            aria-label={seccion.titulo}
            tabIndex={0}
            onKeyDown={alTeclear}
            data-testid={`${p}-galeria`}
          >
            <div className={`${p}-galeria-pista`}>
              {fotos.map((foto, i) => (
                <div
                  className={`${p}-galeria-item`}
                  key={i}
                  aria-roledescription="diapositiva"
                  aria-label={`Foto ${i + 1} de ${fotos.length}`}
                >
                  <button
                    type="button"
                    className={`${p}-galeria-ampliar`}
                    onClick={() => setLightbox(i)}
                    aria-label={`Ampliar foto ${i + 1}`}
                    data-testid={`${p}-galeria-foto-${i}`}
                  >
                    <img src={foto.src} alt={foto.alt || `Fotografía ${i + 1}`} loading="lazy" draggable={false} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className={`${p}-galeria-controles`}>
            <button
              type="button"
              className={`${p}-galeria-flecha`}
              onClick={() => api?.scrollPrev()}
              aria-label="Foto anterior"
              data-testid={`${p}-galeria-anterior`}
            >
              <ChevronLeft strokeWidth={1.5} />
            </button>
            <div className={`${p}-galeria-puntos`} role="tablist" aria-label="Elegir foto">
              {fotos.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`${p}-galeria-punto`}
                  role="tab"
                  aria-current={i === actual}
                  aria-label={`Ir a la foto ${i + 1}`}
                  onClick={() => api?.scrollTo(i)}
                  data-testid={`${p}-galeria-punto-${i}`}
                />
              ))}
            </div>
            <button
              type="button"
              className={`${p}-galeria-flecha`}
              onClick={() => api?.scrollNext()}
              aria-label="Foto siguiente"
              data-testid={`${p}-galeria-siguiente`}
            >
              <ChevronRight strokeWidth={1.5} />
            </button>
          </div>
          <p className={`${p}-sr-only`} aria-live="polite">
            Foto {actual + 1} de {fotos.length}
          </p>
        </Reveal>
      </div>
      {lightbox !== null && (
        <Lightbox key={lightbox} fotos={fotos} inicial={lightbox} alCerrar={cerrarLightbox} p={p} />
      )}
    </section>
  );
}
